<template>
  <div class="ticket-selection" :class="isUpdate && 'is-update'">
    <div class="ticket-selection__container">
      <template v-if="orderType === 'all' && tickets.length">
        <slot>
          <h3 class="ticket-selection__title">
            {{ $t('landingpage.checkout-page.ticket-selection.title') }}
          </h3>

          <div class="ticket-selection__intro">
            <p>{{ $t('landingpage.checkout-page.ticket-selection.intro') }}</p>
          </div>
        </slot>
      </template>

      <div v-if="isFetching">
        <FontAwesomeIcon
          :icon="['fal', 'spinner']"
          class="mx-auto mb-3 block text-2xl"
          spin
        />
      </div>

      <template v-else>
        <div
          v-if="orderType === 'all' && tickets.length"
          class="ticket-selection__list"
        >
          <template v-for="ticket in tickets" :key="ticket.name[activeLocale]">
            <label
              :for="`ticket-selection-item-${ticket.name[activeLocale]}`"
              class="ticket-selection__item"
              :class="{
                'ticket-selection__item--selected':
                  selectedTicket === ticket.id,
                'ticket-selection__item--disabled':
                  ticket.remaining <= 0 || isComingSoon(ticket),
                'ticket-selection__item--coming-soon': isComingSoon(ticket),
              }"
            >
              <input
                :id="`ticket-selection-item-${ticket.name[activeLocale]}`"
                v-model="selectedTicket"
                type="radio"
                name="ticket-selection-input"
                class="ticket-selection__input"
                :value="ticket.id"
                :checked="selectedTicket === ticket.id"
                :disabled="ticket.remaining <= 0 || isComingSoon(ticket)"
                @click="attemptSelectTicket(ticket.id)"
              />

              <div class="ticket-selection__content">
                <div class="ticket-selection__details">
                  <p class="ticket-selection__name">
                    {{ ticket.name[activeLocale] }}
                    <span
                      v-if="
                        ticket.remaining > 0 &&
                        ticket?.price === 0 &&
                        !isComingSoon(ticket)
                      "
                      class="ticket-selection__free"
                    >
                      {{
                        $t('landingpage.checkout-page.ticket-selection.free')
                      }}
                    </span>
                    <span
                      v-if="isComingSoon(ticket)"
                      class="ticket-selection__coming-soon"
                    >
                      {{
                        $t(
                          'landingpage.checkout-page.ticket-selection.coming-soon',
                        )
                      }}
                    </span>
                    <span
                      v-else-if="ticket.remaining <= 0"
                      class="ticket-selection__sold-out"
                    >
                      {{
                        $t(
                          'landingpage.checkout-page.ticket-selection.sold-out',
                        )
                      }}
                    </span>

                    <span
                      v-if="isComingSoon(ticket) && ticket.sales_start"
                      class="ticket-selection__count"
                    >
                      {{
                        $t(
                          'landingpage.checkout-page.ticket-selection.available-from',
                          { date: formatSalesStart(ticket.sales_start) },
                        )
                      }}
                    </span>
                    <span v-else class="ticket-selection__count">
                      {{
                        $t(
                          'landingpage.checkout-page.ticket-selection.available',
                          {
                            count: Math.max(0, ticket.remaining ?? 0),
                          },
                        )
                      }}
                    </span>
                  </p>

                  <p
                    class="ticket-selection__description"
                    v-text="ticket.description[activeLocale]"
                  />
                </div>

                <div class="ticket-selection__price">
                  <p
                    v-if="ticket?.price !== 0"
                    class="ticket-selection__price-value"
                  >
                    {{
                      formatCurrencyLeft(
                        ticket?.price,
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                        orderDetails?.value,
                      )
                    }}
                  </p>

                  <div
                    v-if="selectedTicket === ticket.id"
                    class="ticket-selection__price-icon"
                  >
                    <FontAwesomeIcon :icon="['fas', 'check']" />
                  </div>
                </div>
              </div>
            </label>
          </template>
        </div>

        <AddOns
          v-if="
            orderType === 'add-ons' ||
            (orderType === 'all' && selectedTicket && addOns.length)
          "
          :key="`add-ons-${addOns.length}-${selectedTicket}`"
          :items="addOns"
          :is-update="isUpdate"
          :assign-add-on="assignAddOn"
          @selected="(addOn) => emit('add-on-selected', addOn)"
          @removed="(addOn) => emit('add-on-removed', addOn)"
        />
      </template>
    </div>

    <Alert
      v-if="isConfirmSelectTicketShown"
      show
      :show-alert-icon="false"
      :title="$t('landingpage.checkout-page.select-ticket.confirm.title')"
      :message="$t('landingpage.checkout-page.select-ticket.confirm.message')"
      wrapper-class="select-ticket-alert"
      body-class="py-8! px-4!"
    >
      <template #buttons>
        <div class="flex items-center justify-center gap-2 pt-2">
          <button
            type="button"
            class="btn btn-cancel"
            @click="cancelSelect()"
            v-text="$t('common.cancel')"
          />
          <button
            type="button"
            class="btn btn-primary btn-confirm"
            @click="confirmSelect()"
            v-text="$t('common.confirm')"
          />
        </div>
      </template>
    </Alert>
  </div>
</template>

<script setup>
import { ref, inject, watch, computed } from 'vue'
import AddOns from '@/Components/LandingPage/Checkout/AddOns.vue'
import { formatCurrencyLeft } from '@/composables/currency'
import Alert from '@/Components/Alert.vue'
import {
  formatTicketSalesStart,
  isTicketComingSoon as isComingSoon,
} from '@/LandingPage/Checkout/ticketSalesStatus'

const props = defineProps({
  tickets: {
    type: Array,
    default: () => [],
  },
  value: {
    type: String,
    default: () => '',
  },
  addOns: {
    type: Array,
    default: () => [],
  },
  isFetching: {
    type: Boolean,
    default: () => false,
  },
  isUpdate: {
    type: Boolean,
    default: () => false,
  },
  assignAddOn: {
    type: Function,
    default: null,
  },
})

const emit = defineEmits([
  'ticket-selected',
  'add-on-selected',
  'add-on-removed',
])

const hasAddonItems = computed(() =>
  props.addOns.some((item) =>
    item.addons?.some((addon) => addon.items?.length),
  ),
)

const orderDetails = inject('orderDetails')
const orderType = inject('orderType')
const activeLocale = inject('activeLocale')
const eventSettings = inject('eventSettings', null)

const formatSalesStart = (salesStart) =>
  formatTicketSalesStart(salesStart, eventSettings?.timezone, activeLocale)
const selectedTicket = ref(props.value)
const isConfirmSelectTicketShown = ref(false)
const previousTicket = ref(null) // rollback value
const tempSelectedTicket = ref(null)

watch(
  () => props.value,
  () => (selectedTicket.value = props.value),
)

const attemptSelectTicket = (ticketId) => {
  const ticket = props.tickets.find((t) => t.id === ticketId)
  if (ticket?.remaining <= 0 || isComingSoon(ticket)) return
  if (selectedTicket.value === ticketId) return
  if (
    selectedTicket.value &&
    selectedTicket.value !== ticketId &&
    hasAddonItems.value
  ) {
    previousTicket.value = selectedTicket.value
    tempSelectedTicket.value = ticketId
    isConfirmSelectTicketShown.value = true
  }
  if (!hasAddonItems.value) {
    selectedTicket.value = ticketId
    emit('ticket-selected', selectedTicket.value)
  }
}

const cancelSelect = () => {
  selectedTicket.value = previousTicket.value
  tempSelectedTicket.value = null
  isConfirmSelectTicketShown.value = false
}

const confirmSelect = () => {
  selectedTicket.value = tempSelectedTicket.value
  emit('ticket-selected', selectedTicket.value)
  tempSelectedTicket.value = null
  isConfirmSelectTicketShown.value = false
}
</script>
